/// <reference types="@angular/localize" />

import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injector, enableProdMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { RequiredValidator } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_CONFIG, provideOktaAuth } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { onStartup } from './app/app.module';
import { AppCoreModule } from './app/core/app-core.module';
import { CustomRequiredValidatorDirective } from './app/core/directives/custom-required-validator.directive';
import { ApiBaseUrlInterceptor } from './app/core/interceptors/api-base-url.interceptor';
import { ErrorHandlingInterceptor } from './app/core/interceptors/error-handling.interceptor';
import { OktaTokenInterceptor } from './app/core/interceptors/okta-token.interceptor';
import { CustomDatePipe } from './app/core/pipes/custom-date.pipe';
import { PipesModule } from './app/core/pipes/pipes.module';
import { EnvironmentService } from './app/core/services/environment.service';
import { StartupService } from './app/core/services/startup.service';
import { CustomDateParserFormatter } from './app/core/utils/custom-date-formatter';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const bootstrap = () =>
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        NgIdleKeepaliveModule.forRoot(),
        ToastrModule.forRoot({
          positionClass: 'toast-top-center',
          resetTimeoutOnDuplicate: true,
          progressBar: true,
          closeButton: true,
          timeOut: 5000
        }),
        AppCoreModule,
        AppRoutingModule
      ),
      // TODO: Migrate to withInterceptors() instead
      provideHttpClient(withInterceptorsFromDi()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiBaseUrlInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: OktaTokenInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorHandlingInterceptor,
        multi: true
      },
      provideAppInitializer(() => {
        const initializerFn = onStartup(inject(StartupService));
        return initializerFn();
      }),
      PipesModule,
      {
        provide: DatePipe,
        useExisting: CustomDatePipe
      },
      {
        provide: RequiredValidator,
        useExisting: CustomRequiredValidatorDirective
      },
      {
        provide: NgbDateParserFormatter,
        useClass: CustomDateParserFormatter
      },
      provideOktaAuth(),
      {
        provide: OKTA_CONFIG,
        useFactory: () => {
          const environmentService = inject(EnvironmentService);

          const oktaAuth = new OktaAuth(environmentService.ssoConfig.oidc);

          return {
            oktaAuth,
            // MHNC-1032: Route through /logout instead of /login so that
            // AuthService.logout() runs first — clearing Okta tokens, the
            // server-side session cookie, and backend login state before the
            // sign-in widget initializes. Without this, stale Okta credentials
            // survive and cause getWithoutPrompt() to silently succeed,
            // preventing the widget from rendering.
            onAuthRequired: (_oktaAuth: OktaAuth, injector: Injector) => {
              const router = injector.get(Router);
              router.navigate(['/logout']);
            }
          };
        }
      },
      provideAnimations()
    ]
  });

bootstrap().catch((err) => console.log(err));
